import { StatusBadge } from "@/components/base/StatusBadge";
import { money } from "@/lib/format";
import { categoryPathName, type Category } from "@/lib/categories";
import { CHECK_TARGET, type ActiveTab, type ProductRow, type QualityResult, type SupplierLinkRow } from "../types";
import { sanitizeCatalogueHtml } from "@/lib/catalogue-html";
import ChannelSummaryPanel from "./ChannelSummaryPanel";
import ProductReadinessPanel from "./ProductReadinessPanel";
import { stockPresence } from "@/lib/product-stock-presence";

// Read-only "Overview" feature: a product identity header (image, name,
// status, category, key SKU, price, stock, supplier), quality warnings, the
// description/content, and a supplier summary. It receives data already
// resolved by the orchestrating ProductDetail page and only asks the page to
// switch tabs or open the edit form.

interface Props {
  product: ProductRow;
  quality: QualityResult | null | undefined;
  optionCount: number;
  listingCount: number;
  variantCount: number;
  totalAvailable: number;
  totalOnHand: number;
  priceRange: string;
  costRange: string;
  suppliers: SupplierLinkRow[];
  categoriesList: Category[];
  primaryImageUrl: string | null;
  singleSku: string | null;
  onEdit: () => void;
  onEditPrices?: () => void;
  onGoToTab: (tab: ActiveTab) => void;
  orgId?: string;
  imageCount?: number;
  /** Balance rows actually read. Zero means "nothing posted yet", not "zero stock". */
  stockRowCount?: number;
}

export default function OverviewTab({
  product,
  quality,
  optionCount,
  listingCount,
  variantCount,
  totalAvailable,
  totalOnHand,
  priceRange,
  costRange,
  suppliers,
  categoriesList,
  primaryImageUrl,
  singleSku,
  onEdit,
  onEditPrices,
  onGoToTab,
  orgId,
  imageCount = 0,
  stockRowCount,
}: Props) {
  const p = product;
  const presence = stockPresence({ balanceRowCount: stockRowCount });

  const supplierSummary = (() => {
    if (!suppliers || suppliers.length === 0) return "None";
    const preferred = suppliers.find((s) => s.is_preferred) ?? suppliers[0];
    return preferred.suppliers?.name ?? "None";
  })();

  const isSimple = variantCount <= 1;

  return (
    <div className="space-y-6">
      {/* Identity header — answers the most common questions immediately */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-start gap-5 flex-col sm:flex-row">
          <div className="w-24 h-24 rounded-lg bg-slate-50 border border-slate-200 overflow-hidden flex items-center justify-center shrink-0">
            {primaryImageUrl ? (
              <img src={primaryImageUrl} alt={p.name} className="w-full h-full object-cover object-top" />
            ) : (
              <i className="ri-image-line text-3xl text-slate-300" />
            )}
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <h2 className="text-lg font-semibold text-slate-900">{p.name}</h2>
              <StatusBadge status={p.status} />
            </div>
            <div className="mt-1 flex items-center gap-2 flex-wrap text-sm text-slate-500">
              <span>{categoryPathName(categoriesList, p.category_id) || "Uncategorized"}</span>
              {p.brand && (
                <>
                  <span className="text-slate-300">·</span>
                  <span>{p.brand}</span>
                </>
              )}
              {p.reference && (
                <>
                  <span className="text-slate-300">·</span>
                  <span className="font-mono text-xs">Ref {p.reference}</span>
                </>
              )}
            </div>

            <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div>
                <p className="text-xs text-slate-400">Effective SKU prices</p>
                <p className="text-base font-semibold text-slate-900 mt-0.5">{priceRange}</p>
                <p className="text-xs text-slate-400">
                  Effective cost {costRange}. These ranges are display only.
                </p>
                <p className="text-xs text-slate-400">
                  Product default {product.selling_price != null ? money(product.selling_price) : "not set"}
                  {" · "}cost default {product.cost_price != null ? money(product.cost_price) : "not set"}
                </p>
                <button type="button" onClick={onEdit} className="mt-1 mr-2 text-xs font-medium text-emerald-600 hover:text-emerald-700">
                  Edit product base
                </button>
                {onEditPrices && variantCount > 1 && (
                  <button type="button" onClick={onEditPrices} className="mt-1 text-xs font-medium text-slate-500 hover:text-slate-700">
                    Edit variant exceptions
                  </button>
                )}
              </div>
              <div>
                <p className="text-xs text-slate-400">Available</p>
                {presence.state === "recorded" ? (
                  <>
                    <p className="text-base font-semibold text-emerald-700 mt-0.5">{totalAvailable} available</p>
                    <p className="text-xs text-slate-400">{totalOnHand} on hand · {Math.max(totalOnHand - totalAvailable, 0)} allocated</p>
                  </>
                ) : (
                  <>
                    <p className="text-base font-semibold text-slate-500 mt-0.5">{presence.label}</p>
                    <p className="text-xs text-slate-400" title={presence.detail}>{presence.detail}</p>
                  </>
                )}
              </div>
              <div>
                <p className="text-xs text-slate-400">SKU</p>
                <p className="text-base font-semibold text-slate-900 mt-0.5 font-mono truncate">
                  {singleSku ?? (isSimple ? p.sku ?? "—" : `${variantCount} SKUs`)}
                </p>
                <p className="text-xs text-slate-400">{isSimple ? "simple product" : `${optionCount} options`}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400">Supplier</p>
                <p className="text-base font-semibold text-slate-900 mt-0.5 truncate">{supplierSummary}</p>
                <p className="text-xs text-slate-400">{listingCount} listing{listingCount === 1 ? "" : "s"}</p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={onEdit}
              className="px-3 py-2 bg-slate-900 hover:bg-slate-800 text-white text-xs font-medium rounded-lg whitespace-nowrap flex items-center gap-1.5"
            >
              <i className="ri-pencil-line" /> Edit
            </button>
            <button
              onClick={() => onGoToTab("variants")}
              className="px-3 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-xs font-medium rounded-lg whitespace-nowrap flex items-center gap-1.5"
            >
              <i className="ri-grid-line" /> {isSimple ? "SKU details" : "Variants"}
            </button>
          </div>
        </div>
      </div>

      {quality && quality.checks.length > 0 && (
        <div className="bg-white rounded-xl border border-amber-200 p-5">
          <div className="flex items-center gap-2 mb-3">
            <i className="ri-alert-line text-amber-600" />
            <h3 className="text-sm font-semibold text-slate-800">Needs attention</h3>
            <span className="text-xs text-slate-400">{quality.checks.length} item{quality.checks.length === 1 ? "" : "s"}</span>
          </div>
          <ul className="space-y-1.5">
            {quality.checks.map((c) => {
              const target = CHECK_TARGET[c.code];
              return (
                <li key={c.code} className="flex items-center gap-2 text-sm">
                  <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${c.level === "blocking" ? "bg-red-500" : "bg-amber-400"}`} />
                  <span className={c.level === "blocking" ? "text-red-700" : "text-slate-600"}>{c.message}</span>
                  {target && (
                    <button
                      onClick={() => (target.tab === "overview" ? onEdit() : onGoToTab(target.tab))}
                      className="ml-auto text-xs font-medium text-emerald-600 hover:text-emerald-700 whitespace-nowrap shrink-0"
                    >
                      {target.label}
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {orgId && (
        <ProductReadinessPanel
          orgId={orgId}
          product={p}
          hasSupplier={suppliers.length > 0}
          imageCount={imageCount}
        />
      )}

      {orgId && (
        <ChannelSummaryPanel
          orgId={orgId}
          productId={p.id}
          onManage={() => onGoToTab("channels")}
        />
      )}

      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <h3 className="text-sm font-semibold text-slate-800">Description</h3>
            {p.description ? (
              <div
                className="text-sm text-slate-600 leading-relaxed mt-2 prose prose-sm max-w-none"
                dangerouslySetInnerHTML={{ __html: sanitizeCatalogueHtml(p.description) }}
              />
            ) : (
              <p className="text-sm text-slate-600 leading-relaxed mt-2">No description yet. Add one from Edit to help listings and SEO.</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap mt-4">
          {p.brand && <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{p.brand}</span>}
          {(p.tags ?? []).map((t) => (
            <span key={t} className="text-xs bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full">{t}</span>
          ))}
        </div>
        {(p.short_description || p.seo_title || p.meta_description) && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4 pt-4 border-t border-slate-100">
            <div>
              <p className="text-xs font-medium text-slate-500 mb-1">Short description</p>
              <p className="text-sm text-slate-700">{p.short_description || "—"}</p>
            </div>
            <div>
              <p className="text-xs font-medium text-slate-500 mb-1">SEO title</p>
              <p className="text-sm text-slate-700">{p.seo_title || "—"}</p>
            </div>
            <div>
              <p className="text-xs font-medium text-slate-500 mb-1">Meta description</p>
              <p className="text-sm text-slate-700">{p.meta_description || "—"}</p>
            </div>
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-slate-800">Suppliers</h3>
          <button onClick={() => onGoToTab("suppliers")} className="text-xs font-medium text-emerald-600 hover:text-emerald-700 whitespace-nowrap">Manage</button>
        </div>
        {(suppliers ?? []).length === 0 ? (
          <p className="text-sm text-slate-400">
            No suppliers linked yet. <button onClick={() => onGoToTab("suppliers")} className="text-emerald-600 hover:underline">Add one</button> to see cost, order minimums and lead time.
          </p>
        ) : (
          <div className="space-y-2">
            {(suppliers ?? []).map((l) => (
              <div key={l.id} className="flex items-center justify-between gap-3 text-sm">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="font-medium text-slate-800 truncate">{l.suppliers?.name ?? "—"}</span>
                  {l.is_preferred && <span className="text-[10px] font-semibold bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded-full">Preferred</span>}
                  {l.supplier_sku && <span className="text-xs text-slate-400 font-mono">{l.supplier_sku}</span>}
                </div>
                <div className="text-xs text-slate-500 shrink-0 text-right">
                  {l.supplier_price != null ? <span className="font-semibold text-slate-800">{money(l.supplier_price)}</span> : "—"}
                  {l.min_order_qty ? <span className="ml-2">min {l.min_order_qty}</span> : null}
                  {l.order_multiple && l.order_multiple > 1 ? <span className="ml-2">×{l.order_multiple}</span> : null}
                  {l.lead_time_days ? <span className="ml-2">{l.lead_time_days}d</span> : null}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}